import ScorecardCellsTheme from '../alpine/scorecard-cells-theme-v2.js';
import { safeDeeplink } from './cell-actions.js';

/**
 * A scorecard grid card's table (ScorecardGridSchema; documentation/context/metric-cards.md) as
 * the table entry's mount payload: BlinkTable's columns and rows, plus the per-cell decoration its
 * host options draw (TableCellDecoration in the plugin's types.ts).
 *
 * Pure, and it draws nothing: <x-charts.superset-table> maps every table through it, and the
 * bundle's smoke (standalone/smoke.table.mjs) renders the committed PHP fixtures through it, so
 * the page and the smoke draw one mapping.
 *
 * Nothing is formatted here. Every value, status, change and sentence is PHP's, in the payload;
 * this only chooses where each goes, and the colours of the viewer's palette.
 */

/** The row field holding each row's key (MetricPin::key()), reported back with every cell action. */
export const ROW_KEY_FIELD = '__rowKey';

/** The column of row headers, holding each row's label. */
export const ROW_HEADER_COLUMN = '__rowHeader';

/** The second pinned column, holding each row's goal in a few words. */
export const GOAL_COLUMN = '__goal';

/**
 * What a row header's controls report through onCellAction: its name opening the row's chart, and,
 * while the host edits rows, its remove button and a step up or down from its drag handle's arrow
 * keys (the handle's pointer drag is the host's own, superset-table.js).
 */
export const ROW_ACTIONS = Object.freeze({ open: 'open', remove: 'remove', moveUp: 'move-up', moveDown: 'move-down' });

const NO_DATA_MARK = '–';

const DEEPLINK_GLYPH = '↗';

// The scorecard's own palette table and text-colour rule, read rather than copied, so a grid cell
// is shaded exactly as the viewer's scorecard shades the same bucket.
const scorecardTheme = ScorecardCellsTheme();

/**
 * The fill of one bucket in the viewer's palette (CellBucket::paletteFor()), with the text colour
 * the scorecard pairs with it. Null without a palette, which is how a viewer who chose shapes or
 * icons sees their scorecard: the words carry the status alone.
 */
export function toneFor(palette, bucket) {
    if (! palette || ! bucket) return null;

    const key = `${palette}-${bucket.spectrum}-${bucket.band}`;
    const background = scorecardTheme.THEME_COLORS[key];

    return background ? { background, color: scorecardTheme.getTextColor(key) } : null;
}

function fill(template, values) {
    return Object.entries(values).reduce((text, [token, value]) => text.split(token).join(value), String(template ?? ''));
}

function periodLabel(column) {
    return [column.label, column.subLabel].filter(Boolean).join(' ');
}

/**
 * What a reader can do from one cell: open its drilldown and follow its deeplink. The first becomes
 * the button around the cell's value; a deeplink beside a drilldown is an icon button of its own,
 * so it is named in full. A row whose drilldown this viewer may not open (lockedDrilldownRows)
 * offers them nothing at all, its deeplink included; the page is sent no actions for it
 * (ScorecardGridResult::viewerTable()), and this holds even for a table that still has them.
 */
function actionsFor(row, cell, column, { locked, text }) {
    if (locked.has(row.key)) return undefined;

    const actions = [];

    if (cell.actions?.drilldown) {
        actions.push({ id: 'drilldown', label: text.openDetails });
    }

    if (safeDeeplink(cell.actions?.deeplink)) {
        actions.push(actions.length === 0
            ? { id: 'deeplink', label: text.openSource }
            : { id: 'deeplink', label: fill(text.openSourceFor, { ':row': row.label, ':period': periodLabel(column) }), glyph: DEEPLINK_GLYPH });
    }

    return actions.length > 0 ? actions : undefined;
}

/**
 * One period's cell on one line: the value and its verdict's glyph (the words are the cell's
 * sentence, `srText`, for assistive tech), on the viewer's shade, bold for a record, with its
 * change pill. A period still running reads as a part-period, in grey, with no glyph.
 *
 * The running column instead shows the value so far in grey and the scorecard's projection for the
 * period beside it, as a chip shaded by the projection's own verdict and marked with its glyph, or
 * says it has none: "12, not projected".
 */
function decorationFor(row, cell, column, context) {
    const { palette, text } = context;
    const running = column?.isCurrent === true;
    const inProgress = cell.status?.status === 'in_progress';
    const projection = running ? cell.projection : null;

    return {
        compact: true,
        muted: running || inProgress,
        text: running && ! projection && cell.value !== null ? fill(text.notProjected, { ':value': cell.value }) : undefined,
        status: cell.status ? { text: cell.status.label, glyph: running ? undefined : cell.status.glyph } : undefined,
        srText: cell.srText,
        tone: toneFor(palette, cell.bucket) ?? undefined,
        emphasis: cell.record ? { label: cell.record === 'min' ? text.recordMin : text.recordMax } : undefined,
        chip: projection ? {
            text: projection.value,
            glyph: projection.status?.glyph,
            tone: toneFor(palette, projection.bucket) ?? undefined,
            emphasis: projection.record !== null && projection.record !== undefined,
        } : undefined,
        delta: cell.delta ? { text: cell.delta.text, direction: cell.delta.direction, tone: cell.delta.tone } : undefined,
        actions: actionsFor(row, cell, column, context),
    };
}

/**
 * A row header: the row's name, a button opening its chart where the row offers one and this viewer
 * may open it (never a locked row, never the public link, whose table says no row opens), described
 * by the host's hint rather than words inside it, which would be read as part of the row's name
 * wherever a screen reader names the row. While the host edits rows, a drag handle before the name
 * and a remove button after it. A removed, switched-off or disconnected row's state stays in words
 * under its name.
 */
function rowHeaderFor(row, index, count, context) {
    const { text, locked, editing, gridTitle, rowHints } = context;
    const opens = row.opensChart === true && ! locked.has(row.key);
    const hasRecords = row.cells.some((cell) => cell.actions?.drilldown);
    const filled = (template) => fill(template, { ':metric': row.label, ':grid': gridTitle, ':position': String(index + 1), ':total': String(count) });

    return {
        notes: [row.stateText].filter(Boolean),
        // A narrow grid cuts the name short; the whole name is on hover.
        title: row.label,
        rowHeader: {
            open: opens ? {
                id: ROW_ACTIONS.open,
                describedBy: hasRecords ? rowHints?.records : rowHints?.chart,
                title: filled(hasRecords ? text.openRowTitle : text.openRowChartTitle),
            } : undefined,
            edit: editing ? {
                handleLabel: filled(text.reorderRow),
                removeLabel: filled(text.removeRow),
            } : undefined,
            // Shown under the name only where a phone drops the Goal column.
            goal: row.goal?.text,
        },
    };
}

function indexRows(table) {
    return new Map(table.rows.map((row) => [row.key, { row, cells: new Map(row.cells.map((cell) => [cell.column, cell])) }]));
}

/** The running period's column counted from 1, as CSS's nth-child counts it, or undefined for none. */
function runningColumn(table) {
    const index = table.columns.findIndex((column) => column.isCurrent);

    return index === -1 ? undefined : index + 3;
}

/**
 * A column's header: the period's name over its dates, a line break the table draws as one
 * (freezeEdges). The running period's dates give way to what its cells show, the value so far and
 * the projection.
 */
function columnLabel(column, text) {
    const under = column.isCurrent ? text.soFarProjected : column.subLabel;

    return [column.label, under].filter(Boolean).join('\n');
}

/**
 * @param {object} table  ScorecardGridSchema's payload
 * @param {object} options
 * @param {?string}  options.palette              CellBucket::paletteFor() for the viewer
 * @param {object}   options.text                 the host's translated strings
 * @param {string[]} [options.lockedDrilldownRows] rows whose drilldown this viewer may not open, which offer them no action
 * @param {Function} [options.onCellAction]       ({rowKey, columnKey, action, trigger}) => void, for a cell's buttons and a row header's (ROW_ACTIONS)
 * @param {boolean}  [options.editing]            the host edits rows: each row header gets a drag handle and a remove button
 * @param {string}   [options.gridTitle]          the grid's name, in the remove button's
 * @param {?{records: string, chart: string}} [options.rowHints] the ids of the host's descriptions of what a row's name opens: its chart and records, or its chart alone
 * @param {string}   [options.tableLabel]         the table's name, when no element names it
 * @param {string}   [options.tableLabelledBy]    the id of the element naming it
 * @param {string}   [options.idPrefix]           unique per mount on the page
 * @param {number}   [options.height]
 * @param {number}   [options.width]
 */
export function gridMountPayload(table, {
    palette = null,
    text = {},
    lockedDrilldownRows = [],
    onCellAction,
    editing = false,
    gridTitle = '',
    rowHints = null,
    tableLabel,
    tableLabelledBy,
    idPrefix,
    height,
    width,
} = {}) {
    const rows = indexRows(table);
    const columns = new Map(table.columns.map((column) => [column.key, column]));
    const positions = new Map(table.rows.map((row, index) => [row.key, index]));
    const context = { palette, text, locked: new Set(lockedDrilldownRows), editing: editing === true, gridTitle, rowHints };

    const describeCell = (record, columnKey) => {
        const entry = rows.get(record[ROW_KEY_FIELD]);
        if (! entry) return null;

        if (columnKey === ROW_HEADER_COLUMN) {
            return rowHeaderFor(entry.row, positions.get(entry.row.key), table.rows.length, context);
        }

        // The goal in a few words, and in full for assistive tech; a removed row has none to state.
        if (columnKey === GOAL_COLUMN) {
            if (entry.row.goal) return { srText: entry.row.goal.label, title: entry.row.goal.label };

            return entry.row.state === 'missing' ? null : { srText: text.noGoal };
        }

        const cell = entry.cells.get(columnKey);

        return cell ? decorationFor(entry.row, cell, columns.get(columnKey), context) : null;
    };

    return {
        columns: [
            { key: ROW_HEADER_COLUMN, label: text.rowHeader ?? '', type: 'string' },
            { key: GOAL_COLUMN, label: text.goalHeader ?? '', type: 'string' },
            ...table.columns.map((column) => ({ key: column.key, label: columnLabel(column, text), type: 'string' })),
        ],
        rows: table.rows.map((row) => {
            const isRemoved = row.state === 'missing';
            const record = {
                [ROW_KEY_FIELD]: row.key,
                [ROW_HEADER_COLUMN]: row.label,
                [GOAL_COLUMN]: row.goal?.text ?? (isRemoved ? '' : NO_DATA_MARK),
            };
            const cells = rows.get(row.key).cells;

            // A removed row has no cells and reads empty; a period with no figure shows a dash,
            // and its sentence says "no data".
            for (const column of table.columns) {
                const cell = cells.get(column.key);
                record[column.key] = cell ? (cell.value ?? NO_DATA_MARK) : '';
            }

            return record;
        }),
        options: {
            // The rows format only: the pinned order, no search, no pages, no totals.
            pageSize: 0,
            includeSearch: false,
            showCellBars: false,
            showTotals: false,
            sortable: false,
            rowStriping: false,
            allowRenderHtml: false,
            // Drawn whole inside the host's own scroll port, with the header row, the row headers
            // and the goals pinned, so there is one scrolling box and one real <table>.
            sticky: false,
            freezeEdges: true,
            frozenColumns: 2,
            // The running period's column, counted from 1 past the Metric and Goal columns, tinted.
            runningColumn: runningColumn(table),
            rowKeyField: ROW_KEY_FIELD,
            rowHeaderColumn: ROW_HEADER_COLUMN,
            describeCell,
            onCellAction,
            tableLabel,
            tableLabelledBy,
            idPrefix,
            height,
            width,
        },
    };
}

/**
 * What one pressed action opens, looked up in the table again rather than taken from the page:
 * `{kind: 'drilldown', detail}` with the `show-drilldown-modal` event's keys, `{kind: 'deeplink',
 * url}`, or null for an action the table does not offer this viewer (any action on a locked row).
 */
export function resolveCellAction(table, { rowKey, columnKey, action }, { lockedDrilldownRows = [] } = {}) {
    if (lockedDrilldownRows.includes(rowKey)) return null;

    const row = table?.rows?.find((candidate) => candidate.key === rowKey);
    const cell = row?.cells?.find((candidate) => candidate.column === columnKey);

    if (action === 'drilldown' && cell?.actions?.drilldown) {
        return { kind: 'drilldown', detail: { ...cell.actions.drilldown } };
    }

    const url = action === 'deeplink' ? safeDeeplink(cell?.actions?.deeplink) : null;

    return url ? { kind: 'deeplink', url } : null;
}

/**
 * What a row header's pressed control asks for, looked up in the table again rather than taken from
 * the page: `{kind: 'open', row}` for a row whose name opens its chart for this viewer,
 * `{kind: 'remove', row}` and `{kind: 'move', row, toIndex}` while the host edits rows, or null for
 * anything the table does not offer (a locked row's name, an edit control outside edit mode, a
 * step past either end). `order` is the rows' keys as the host last asked for them, so a second
 * step taken before the grid is drawn again counts from where the first one put the row.
 */
export function resolveRowAction(table, { rowKey, action }, { lockedDrilldownRows = [], editing = false, order = null } = {}) {
    const row = table?.rows?.find((candidate) => candidate.key === rowKey);

    if (! row) return null;

    if (action === ROW_ACTIONS.open) {
        return row.opensChart === true && ! lockedDrilldownRows.includes(rowKey) ? { kind: 'open', row } : null;
    }

    if (! editing) return null;

    if (action === ROW_ACTIONS.remove) return { kind: 'remove', row };

    const keys = Array.isArray(order) && order.includes(rowKey) ? order : table.rows.map((candidate) => candidate.key);
    const from = keys.indexOf(rowKey);
    const toIndex = action === ROW_ACTIONS.moveUp ? from - 1 : action === ROW_ACTIONS.moveDown ? from + 1 : -1;

    return toIndex >= 0 && toIndex < keys.length ? { kind: 'move', row, toIndex } : null;
}

/** `keys` with `rowKey` moved to `toIndex`, clamped into them, as ReportArrangement::moveRow() moves it. */
export function movedOrder(keys, rowKey, toIndex) {
    const from = keys.indexOf(rowKey);

    if (from === -1) return [...keys];

    const next = keys.filter((key) => key !== rowKey);
    next.splice(Math.max(0, Math.min(keys.length - 1, toIndex)), 0, rowKey);

    return next;
}

/**
 * Where a row dragged from `from` lands for a pointer at `y`, given each row's vertical middle, top
 * to bottom: past a row's middle is past the row. The row's own place is `from`.
 */
export function dropIndex(middles, from, y) {
    const passed = middles.filter((middle) => middle < y).length;
    const index = passed > from ? passed - 1 : passed;

    return Math.max(0, Math.min(middles.length - 1, index));
}
